import styles from './index.css';

// Please replace, if IE11-support isn't required any longer.
//import {useFormContext} from 'react-hook-form';
import {useFormContext} from 'react-hook-form/dist/react-hook-form.ie11';

import * as React from 'react';
import classNames from 'classnames';

import {DataSource, FormFieldProps} from '../../Definitions';
import {FormField, Block} from '../../LayoutComponents';
import PostalAddress from './PostalAddress';

const PATTERN_POSTALCODE = /^\d{4,5}$/
const NO_SCHOOL_MATCH = 'NO_SCHOOL_MATCH';

type School = {
	identifier: string;
	name: string;
	streetAddress?: string;
	postalCode?: string;
	addressCountry?: string;
	addressLocality?: string;
};

type InstitutionSelectProps = {
	withInstitutions: (data?: any) => DataSource;
	getConfig: (key: string) => FormFieldProps;
	getLabel: (key: string) => string;
}
export default ({withInstitutions, getLabel, getConfig}: InstitutionSelectProps) => {
	const [isManualAddress, setIsManualAddress] = React.useState(false);
	const {data, isLoading, refresh} = withInstitutions();
	const {setValue, getValues, watch, clearError} = useFormContext();
	const postalCodeQuery = getValues()['hidden.postalCode'];
	const selectedSchoolIdentifier = getValues()['hidden.school'];
	const noMatchingSchools = !isManualAddress && !isLoading && Array.isArray(data) && data.length === 0;

	watch(['hidden.postalCode', 'participant', 'hidden.school']);

	React.useEffect(() => {
		if (PATTERN_POSTALCODE.test(postalCodeQuery)) {
			setIsManualAddress(false);
			refresh({postalCodeQuery});
		}
	}, [postalCodeQuery]);

	React.useEffect(() => {
		if (selectedSchoolIdentifier !== NO_SCHOOL_MATCH && Array.isArray(data)) {
			const school = data.find(({identifier}: School) => selectedSchoolIdentifier === identifier);

			if (school) {
				const {name, streetAddress, postalCode, addressLocality} = school;
				setValue('participant.name', name);
				setValue('participant.address.streetAddress', streetAddress);
				setValue('participant.address.postalCode', postalCode);
				setValue('participant.address.addressLocality', addressLocality);
				return;
			}
		}

		setValue('participant.name', '');
		setValue('participant.address.streetAddress', '');
		setValue('participant.address.postalCode', postalCodeQuery || '');
		setValue('participant.address.addressLocality', '');

		if (selectedSchoolIdentifier === NO_SCHOOL_MATCH) {
			setIsManualAddress(true);
		}
	}, [selectedSchoolIdentifier]);

	const hasSelectedSchool = !isManualAddress && Boolean(selectedSchoolIdentifier);
	const willBeValidatedByManualInputs = isManualAddress && !noMatchingSchools;
	const isPostalCodeRequired = !(hasSelectedSchool || willBeValidatedByManualInputs);
	React.useEffect(() => {
		clearError('hidden.postalCode');
	}, [isPostalCodeRequired]);

	return (
		<Block>
			<FormField
				type="TextInput"
				name="hidden.postalCode"
				validate={() => !isPostalCodeRequired}
				required={isPostalCodeRequired}
				label={getLabel('participant.postalCodeQuery')}
			/>
			{
				isLoading && (
					<Block>
						<div className={styles.loader}>{getLabel('participant.searching')}</div>
					</Block>
				)
			}
			{
				noMatchingSchools && (
					<Block>
						<div className={styles.noMatch}>{getLabel('participant.noMatchingSchools')}</div>
					</Block>
				)
			}
			{
				data && data.length > 0 && !isManualAddress &&
					<FormField
						type="Selectbox"
						name="hidden.school"
						label={getLabel('participant.selectSchool')}
						required={!isManualAddress}
						options={[
							{
								value: NO_SCHOOL_MATCH,
								content: (
									<div className={styles.school}>
										{getLabel('participant.selectManual')}
									</div>
								)
							},
							...data.map(
								(school: School) => ({
									value: school.identifier,
									label: school.name,
									content: (
										<div className={styles.school}>
											{
												[
													school.name,
													school.streetAddress,
													school.postalCode,
													school.addressLocality,
												]
													.filter(i => i)
													.join(', ')
											}
										</div>
									)
								})
							)
						]}
					/>
			}
			{
				!isManualAddress && !isLoading && (
					<Block>
						<div
							className={styles.setManual}
							onClick={() => setIsManualAddress(true)}
						>
							{getLabel('participant.manualInput')}
						</div>
					</Block>
				)
			}
			<div className={classNames(!isManualAddress && styles.hidden, styles.block)}>
				<FormField {...getConfig('participant.name')}/>
				<PostalAddress name="participant.address" {...{getConfig, getLabel}}/>
			</div>
		</Block>
	);
}
